/**
 * Madesa Header JavaScript - Based on Figma Designs
 * Handles announcement carousel, search, CEP modal, account dropdown,
 * megamenu, mobile menu, cart, and countdown timer functionality
 */

function interpolateHeaderI18n(template, vars) {
    if (!template) return '';
    return String(template).replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, key) {
        return vars[key] != null ? String(vars[key]) : '';
    });
}

function escapeHtml(text) {
    if (text == null) return '';
    return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function parseHeaderMadesaI18n() {
    const defaults = {
        deliver_prefix: 'Deliver to',
        search_loading: 'Searching…',
        search_empty_message: 'See full results for "{{ query }}".',
        search_view_all: 'See all results',
        search_view_all_for: 'See all results for "{{ query }}"',
        search_error: 'Could not load products. Please try again.',
        search_more_colors: '+ {{ count }} colors',
        cep_invalid_alert: 'Invalid postal code. Enter an 8-digit postal code.',
        loading: 'Loading…',
        cep_not_found_alert: 'Postal code not found. Check the number and try again.',
        enter_cep: 'Enter your postal code',
        geo_unavailable: 'Geolocation is not available in your browser.',
        geo_fetching: 'Getting location…',
        geo_reverse_failed: 'Could not get a postal code from your location. Please enter it manually.',
        geo_permission_denied: 'Permission denied. Enable location in your browser settings.',
        geo_position_unavailable: 'Location is unavailable right now.',
        geo_timeout: 'Timed out while getting location.',
        geo_generic_error: 'Could not get your location.',
        price_for_label: 'for:',
        pix_price_line: 'Pay in full with PIX ({{ discount }}% OFF)',
        money_locale: (typeof window !== 'undefined' && window.Shopify && window.Shopify.locale),
        money_currency: (typeof window !== 'undefined' && window.Shopify && window.Shopify.currency && window.Shopify.currency.active),
        money_symbol: '$'
    };
    const el = document.getElementById('header-madesa-i18n');
    if (!el || !String(el.textContent || '').trim()) {
        return defaults;
    }
    try {
        const parsed = JSON.parse(el.textContent);
        const out = { ...defaults };
        for (const key of Object.keys(parsed)) {
            if (parsed[key] != null && parsed[key] !== '') {
                out[key] = parsed[key];
            }
        }
        return out;
    } catch (error) {
        return defaults;
    }
}

class MadesaHeader {
    constructor() {
        this.str = parseHeaderMadesaI18n();
        /** @type {AbortController | null} */
        this._activeSearchFetch = null;
        this.init();
    }

    h(key, vars) {
        return interpolateHeaderI18n(this.str[key], vars);
    }

    pixPriceLine(discount) {
        return this.h('pix_price_line', { discount: discount });
    }

    pixOnProductCard() {
        return typeof window.madesaPixEnabled === 'function'
            ? window.madesaPixEnabled('productCard')
            : true;
    }

    searchDisplayPrice(productPrice) {
        if (!this.pixOnProductCard()) {
            return this.formatMoney(productPrice);
        }
        const percent = parseFloat(window.generalPixDiscount || '0') || 0;
        if (percent <= 0) {
            return this.formatMoney(productPrice);
        }
        return this.formatMoney(productPrice * (1 - percent / 100));
    }

    init() {
        // Initialize all components
        this.initSearch();
        this.initCEPModal();
        this.initAccountDropdown();
        this.initMegamenu();
        this.initMobileMenu();
        this.initCountdown();

        // Check for CEP parameter in URL
        this.checkURLForCEP();

        // Listen for CEP updates from other components (e.g., shipping-rates.js, madesa-cart-shipping.js)
        document.addEventListener('cep:updated', (event) => {
            this.updateHeaderFromEvent(event.detail);
        });
    }

    /**
     * Check URL for CEP parameter and update if present
     */
    checkURLForCEP() {
        const urlParams = new URLSearchParams(window.location.search);
        const cepParam = urlParams.get('cep') || urlParams.get('CEP');

        if (cepParam) {
            // Clean the CEP parameter (remove any non-numeric characters)
            const cleanCEP = cepParam.replace(/\D/g, '');

            // Validate CEP length
            if (cleanCEP.length === 8) {
                // Update CEP automatically
                this.updateCEP(cleanCEP);
            } else {
                console.warn(`CEP inválido na URL: ${cepParam}. O CEP deve ter 8 dígitos.`);
            }
        }
    }

    updateHeaderFromEvent(addressData) {
        // Update desktop CEP display: "Entregar em Cidade, UF - CEP"
        const desktopAddressTexts = document.querySelectorAll('.madesa-address-text');
        desktopAddressTexts.forEach(container => {
            // Verifica se já existe um label, se não, cria
            let label = container.querySelector('.madesa-address-label');
            if (!label) {
                label = document.createElement('span');
                label.className = 'madesa-address-label';
                label.textContent = this.str.deliver_prefix;
                container.insertBefore(label, container.firstChild);
            }

            // Atualiza o valor
            const valueEl = container.querySelector('.madesa-address-value');
            if (valueEl) {
                valueEl.textContent = `${addressData.city}, ${addressData.state} - ${addressData.cep}`;
            }
        });

        const mobileCepText = document.querySelector('.madesa-mobile-cep-text');
        if (mobileCepText) {
            
            let label = mobileCepText.querySelector('.madesa-address-label');
            if (!label) {
                label = document.createElement('span');
                label.className = 'madesa-address-label';
                label.textContent = this.str.deliver_prefix;
                mobileCepText.insertBefore(label, mobileCepText.firstChild);
            }

            let valueEl = mobileCepText.querySelector('.madesa-cep-default');
            if (!valueEl) {
                valueEl = document.createElement('span');
                valueEl.className = 'madesa-cep-default';
                mobileCepText.appendChild(valueEl);
            }
            valueEl.textContent = `${addressData.city}, ${addressData.state} - ${addressData.cep}`;
        }
    }

    buildProductSearchUrl(query) {
        const build = globalThis.MadesaSearchRelevance?.buildProductSearchUrl;
        if (typeof build === 'function') {
            return build(query);
        }
        const base =
            typeof Theme !== 'undefined' && Theme.routes && Theme.routes.search_url
                ? Theme.routes.search_url
                : '/search';
        const params = new URLSearchParams();
        params.set('q', String(query || '').trim().toLowerCase());
        params.set('type', 'product');
        return `${base}?${params.toString()}`;
    }

    async navigateToSearch(query) {
        const q = String(query || '').trim();
        if (!q) return;
        const nav = globalThis.MadesaSearchRelevance?.navigateForSearchQuery;
        if (typeof nav === 'function') {
            await nav(q);
            return;
        }
        window.location.href = this.buildProductSearchUrl(q);
    }

    // ========== Search Modal ==========
    initSearch() {
        const searchTriggers = document.querySelectorAll('[data-search-trigger]');
        const searchField = document.querySelector('[data-search-field]');
        const searchModal = document.querySelector('[data-search-modal]');
        const searchClose = document.querySelector('[data-search-close]');
        const searchInput = document.querySelector('[data-search-input]');
        const searchOverlay = document.querySelector('[data-search-overlay]');
        const searchResults = document.querySelector('[data-search-results]');
        const searchDefault = document.querySelector('[data-search-default]');

        if (!searchTriggers.length) return;

        let searchTimeout = null;

        searchModal?.addEventListener('click', (e) => {
            const viewAll = e.target.closest('.madesa-search-view-all-btn');
            if (!viewAll) return;
            e.preventDefault();
            const q = viewAll.dataset.searchQuery || searchInput?.value?.trim();
            if (q) this.navigateToSearch(q);
        });

        searchTriggers.forEach(trigger => {
            trigger.addEventListener('click', () => {
                this.openModal(searchModal);
                if (searchInput) {
                    setTimeout(() => searchInput.focus(), 100);
                }
            });
        });

        // Make search field button clickable to focus input
        if (searchField && searchInput) {
            searchField.addEventListener('click', () => {
                searchInput.focus();
            });
        }

        if (searchClose) {
            searchClose.addEventListener('click', () => {
                this.closeModal(searchModal);
                this.clearSearch(searchInput, searchResults, searchDefault);
            });
        }

        // Close on escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && searchModal && !searchModal.classList.contains('madesa-hidden')) {
                this.closeModal(searchModal);
                this.clearSearch(searchInput, searchResults, searchDefault);
            }
        });

        // Close on backdrop/overlay click
        if (searchOverlay) {
            searchOverlay.addEventListener('click', () => {
                this.closeModal(searchModal);
                this.clearSearch(searchInput, searchResults, searchDefault);
            });
        }

        // Predictive search on input
        if (searchInput) {
            searchInput.addEventListener('input', (e) => {
                const query = e.target.value.trim();

                // Clear previous timeout
                if (searchTimeout) {
                    clearTimeout(searchTimeout);
                }

                // Show default content if query is empty
                if (query.length === 0) {
                    if (searchResults) searchResults.classList.add('madesa-hidden');
                    if (searchDefault) searchDefault.classList.remove('madesa-hidden');
                    return;
                }

                // Wait 300ms before searching
                searchTimeout = setTimeout(() => {
                    this.performSearch(query, searchResults, searchDefault);
                }, 300);
            });

            // Handle enter key to go to search results page
            searchInput.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    const query = searchInput.value.trim();
                    if (query) {
                        this.navigateToSearch(query);
                    }
                }
            });
        }
    }

    performSearch(query, resultsContainer, defaultContainer) {
        if (!resultsContainer) return;

        if (this._activeSearchFetch) {
            this._activeSearchFetch.abort();
        }
        this._activeSearchFetch = new AbortController();
        const { signal } = this._activeSearchFetch;

        // Show loading state
        resultsContainer.innerHTML = '<div class="madesa-search-loading" style="padding: 40px 24px;">' + escapeHtml(this.str.search_loading) + '</div>';
        resultsContainer.classList.remove('madesa-hidden');
        if (defaultContainer) defaultContainer.classList.add('madesa-hidden');

        // Use Shopify's predictive search API (fields include variants.sku — not in API default)
        fetch(
            `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=10&resources[options][fields]=title,product_type,variants.title,vendor,variants.sku,tag`,
            { signal }
        )
            .then(response => response.json())
            .then(data => {
                if (signal.aborted) return;
                let products = data.resources.results.products || [];
                const rank = globalThis.MadesaSearchRelevance?.sortProductsByQuery;
                if (typeof rank === 'function' && query.trim()) {
                    products = rank(products, query);
                }

                if (products.length === 0) {
                    resultsContainer.innerHTML = `
                        <div class="madesa-search-empty" style="padding: 40px 24px;">
                        <p>${this.h('search_empty_message', { query: escapeHtml(query) })}</p>
                        <a href="${this.buildProductSearchUrl(query)}" class="madesa-search-view-all-btn" data-search-query="${escapeHtml(query)}">
                            ${escapeHtml(this.str.search_view_all)}
                        </a>
                        </div>
                    `;
                    return;
                }

                // Build results HTML with full product cards
                resultsContainer.innerHTML = `
                <div class="madesa-search-products-grid">
                    ${products.map(product => {
                    const hasDiscount = product.compare_at_price_max && product.compare_at_price_max > product.price;
                    const variantCount = product.variants.length || 1;

                    return `
                            <a href="${product.url}" class="madesa-search-product-card">
                                <div class="madesa-search-product-image">
                                ${product.featured_image ? `
                                    <img src="${product.featured_image.url}" alt="${product.title}" loading="lazy">
                                ` : `
                                    <div class="madesa-search-product-placeholder">
                                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                                        <rect width="48" height="48" rx="4" fill="#f5f5f5"/>
                                        <path d="M24 14v20M14 24h20" stroke="#d0d0d0" stroke-width="2" stroke-linecap="round"/>
                                    </svg>
                                    </div>
                                `}
                                
                                ${variantCount > 1 ? `
                                    <div class="madesa-color-badge madesa-color-badge--search">
                                    ${escapeHtml(interpolateHeaderI18n(this.str.search_more_colors, { count: variantCount - 1 }))}
                                    </div>
                                ` : ''}
                                </div>
                                
                                <div class="madesa-search-product-info">
                                <h4 class="madesa-search-product-title">${product.title}</h4>
                                
                                <div class="madesa-search-product-pricing">
                                    ${hasDiscount ? `
                                    <div class="madesa-search-product-price-compare">${this.formatMoney(product.compare_at_price_max)}</div>
                                    ` : ''}
                                    <div class="madesa-search-product-price-row">
                                    <span class="madesa-search-product-price-label">${escapeHtml(this.str.price_for_label)}</span>
                                    <span class="madesa-search-product-price-current">${this.searchDisplayPrice(product.price)}</span>
                                    </div>
                                    ${this.pixOnProductCard() ? `
                                    <div class="madesa-search-product-price-installment">
                                    ${escapeHtml(this.pixPriceLine(parseFloat(window.generalPixDiscount || '0') || 0))}
                                    </div>
                                    ` : ''}
                                </div>
                                </div>
                            </a>
                            `;
                }).join('')}
                        </div>
                        <div style="padding: 0; margin-top: 16px;">
                            <a href="${this.buildProductSearchUrl(query)}" class="madesa-search-view-all-btn" data-search-query="${escapeHtml(query)}">
                            ${this.h('search_view_all_for', { query: escapeHtml(query) })}
                            </a>
                        </div>
                        `;

                const listEl = resultsContainer.querySelector('.madesa-search-products-grid') || resultsContainer;
                listEl.dispatchEvent(new CustomEvent('madesa:search-list-refresh', { bubbles: true }));
            })
            .catch(error => {
                if (signal?.aborted) return;
                console.error('Search error:', error);
                resultsContainer.innerHTML = `
                    <div class="madesa-search-error" style="padding: 40px 24px;">
                        <p>${escapeHtml(this.str.search_error)}</p>
                    </div>`;
            });
    }
  
    clearSearch(input, results, defaultContainer) {
        if (input) input.value = '';
        if (results) results.classList.add('madesa-hidden');
        if (defaultContainer) defaultContainer.classList.remove('madesa-hidden');
    }

    formatMoney(cents) {
        const locale = this.str.money_locale;
        const symbol = this.str.money_symbol;
        const formatted = new Intl.NumberFormat(locale, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(Number(cents) || 0);
        return `${symbol}${formatted}`;
    }

    // ========== CEP/Address Modal ==========
    initCEPModal() {
        const cepTriggers = document.querySelectorAll('[data-cep-trigger]');
        const cepModal = document.querySelector('[data-cep-modal]');
        const cepClose = document.querySelector('[data-cep-close]');
        const cepOverlay = document.querySelector('[data-cep-overlay]');
        const cepForm = document.querySelector('[data-cep-form]');
        const cepInput = document.querySelector('[data-cep-input]');
        const useLocationBtn = document.querySelector('[data-use-location]');

        if (!cepTriggers.length) return;

        cepTriggers.forEach(trigger => {
            trigger.addEventListener('click', () => {
                this.openModal(cepModal);
                if (cepInput) {
                    setTimeout(() => cepInput.focus(), 100);
                }
            });
        });

        if (cepClose) {
            cepClose.addEventListener('click', () => {
                this.closeModal(cepModal);
            });
        }

        if (cepOverlay) {
            cepOverlay.addEventListener('click', () => {
                this.closeModal(cepModal);
            });
        }

        if (cepForm) {
            cepForm.addEventListener('submit', (e) => {
                e.preventDefault();
                const cepValue = cepInput?.value.trim();
                if (cepValue) {
                    this.updateCEP(cepValue);
                    this.closeModal(cepModal);
                }
            });
        }

        // Format CEP as user types
        if (cepInput) {
            cepInput.addEventListener('input', (e) => {
                let value = e.target.value.replace(/\D/g, '');
                if (value.length > 5) {
                    value = value.slice(0, 5) + '-' + value.slice(5, 8);
                }
                e.target.value = value;
            });
        }

        // Use geolocation
        if (useLocationBtn) {
            useLocationBtn.addEventListener('click', () => {
                this.useGeolocation(cepModal);
            });
        }

        // Close on escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && cepModal && !cepModal.classList.contains('madesa-hidden')) {
                this.closeModal(cepModal);
            }
        });

        // Load saved address on page load
        this.loadSavedAddress();
    }

    loadSavedAddress() {
        const savedAddress = localStorage.getItem('madesa-address');

        if (savedAddress) {
            try {
                const addressData = JSON.parse(savedAddress);

                // Update desktop CEP display
                const desktopAddressTexts = document.querySelectorAll('.madesa-address-text');
                desktopAddressTexts.forEach(container => {
                    // Verifica se já existe um label, se não, cria
                    let label = container.querySelector('.madesa-address-label');
                    if (!label) {
                        label = document.createElement('span');
                        label.className = 'madesa-address-label';
                        label.textContent = this.str.deliver_prefix;
                        container.insertBefore(label, container.firstChild);
                    }

                    // Atualiza o valor
                    const valueEl = container.querySelector('.madesa-address-value');
                    if (valueEl) {
                        valueEl.textContent = `${addressData.city}, ${addressData.state} - ${addressData.cep}`;
                    }
                });

                // Update mobile CEP display
                const mobileCepText = document.querySelector('.madesa-mobile-cep-text');
                if (mobileCepText) {
                    // Verifica se já existe um label, se não, cria
                    let label = mobileCepText.querySelector('.madesa-address-label');
                    if (!label) {
                        label = document.createElement('span');
                        label.className = 'madesa-address-label';
                        label.textContent = this.str.deliver_prefix;
                        mobileCepText.insertBefore(label, mobileCepText.firstChild);
                    }

                    // Atualiza ou cria o elemento de valor
                    let valueEl = mobileCepText.querySelector('.madesa-cep-default');
                    if (!valueEl) {
                        valueEl = document.createElement('span');
                        valueEl.className = 'madesa-cep-default';
                        mobileCepText.appendChild(valueEl);
                    }
                    valueEl.textContent = `${addressData.city}, ${addressData.state} - ${addressData.cep}`;
                }

                // Trigger event for other components
                document.dispatchEvent(new CustomEvent('cep:updated', {
                    detail: addressData
                }));
            } catch (error) {
                console.error('Erro ao carregar endereço salvo:', error);
            }
        }
    }

    updateCEP(cep) {
        // Remove any non-numeric characters from CEP
        const cleanCEP = cep.replace(/\D/g, '');

        if (cleanCEP.length !== 8) {
            alert(this.str.cep_invalid_alert);
            return;
        }

        // Show loading state
        const desktopAddressTexts = document.querySelectorAll('.madesa-address-text');
        const mobileCepTextContainer = document.querySelector('.madesa-mobile-cep-text');

        desktopAddressTexts.forEach(container => {
            const valueEl = container.querySelector('.madesa-address-value');
            if (valueEl) {
                valueEl.textContent = this.str.loading;
            }
        });

        if (mobileCepTextContainer) {
            const mobileCepValue = mobileCepTextContainer.querySelector('.madesa-cep-default');
            if (mobileCepValue) {
                mobileCepValue.textContent = this.str.loading;
            }
        }

        // Call Brasil API
        fetch(`https://brasilapi.com.br/api/cep/v1/${cleanCEP}`)
            .then(response => response.json())
            .then(data => {
                if (data.erro) {
                    throw new Error('CEP não encontrado');
                }

                // Format CEP with hyphen
                const formattedCEP = `${cleanCEP.slice(0, 5)}-${cleanCEP.slice(5)}`;

                // Get address data from Brasil API
                const addressData = {
                    cep: formattedCEP,
                    city: data.city,
                    state: data.state,
                    neighborhood: data.neighborhood,
                    street: data.street
                };

                localStorage.setItem('madesa-address', JSON.stringify(addressData));


                // Update desktop CEP display: "Entregar em Cidade, UF - CEP"
                desktopAddressTexts.forEach(container => {
                    // Verifica se já existe um label, se não, cria
                    let label = container.querySelector('.madesa-address-label');
                    if (!label) {
                        label = document.createElement('span');
                        label.className = 'madesa-address-label';
                        label.textContent = this.str.deliver_prefix;
                        container.insertBefore(label, container.firstChild);
                    }

                    // Atualiza o valor
                    const valueEl = container.querySelector('.madesa-address-value');
                    if (valueEl) {
                        valueEl.textContent = `${addressData.city}, ${addressData.state} - ${formattedCEP}`;
                    }
                });

                // Update mobile CEP display
                if (mobileCepTextContainer) {
                    // Verifica se já existe um label, se não, cria
                    let label = mobileCepTextContainer.querySelector('.madesa-address-label');
                    if (!label) {
                        label = document.createElement('span');
                        label.className = 'madesa-address-label';
                        label.textContent = this.str.deliver_prefix;
                        mobileCepTextContainer.insertBefore(label, mobileCepTextContainer.firstChild);
                    }

                    // Atualiza ou cria o elemento de valor
                    let mobileCepValue = mobileCepTextContainer.querySelector('.madesa-cep-default');
                    if (!mobileCepValue) {
                        mobileCepValue = document.createElement('span');
                        mobileCepValue.className = 'madesa-cep-default';
                        mobileCepTextContainer.appendChild(mobileCepValue);
                    }
                    mobileCepValue.textContent = `${addressData.city}, ${addressData.state} - ${formattedCEP}`;
                }

                // Trigger custom event for other components (e.g., shipping calculator)
                document.dispatchEvent(new CustomEvent('cep:updated', {
                    detail: addressData
                }));
            })
            .catch(error => {
                console.error('Erro ao buscar CEP:', error);

                // Restore default text
                desktopAddressTexts.forEach(container => {
                    // Remove o label se existir
                    const label = container.querySelector('.madesa-address-label');
                    if (label) {
                        label.remove();
                    }

                    const valueEl = container.querySelector('.madesa-address-value');
                    if (valueEl) {
                        valueEl.textContent = this.str.enter_cep;
                    }
                });

                if (mobileCepTextContainer) {
                    // Remove o label se existir
                    const label = mobileCepTextContainer.querySelector('.madesa-address-label');
                    if (label) {
                        label.remove();
                    }

                    const mobileCepValue = mobileCepTextContainer.querySelector('.madesa-cep-default');
                    if (mobileCepValue) {
                        mobileCepValue.textContent = this.str.enter_cep;
                    }
                }

                alert(this.str.cep_not_found_alert);
            });
    }

    useGeolocation(modal) {
        if (!navigator.geolocation) {
            alert(this.str.geo_unavailable);
            return;
        }

        // Show loading state
        const useLocationBtn = document.querySelector('[data-use-location]');
        if (useLocationBtn) {
            useLocationBtn.disabled = true;
            const originalText = useLocationBtn.innerHTML;
            useLocationBtn.innerHTML = '<span>' + escapeHtml(this.str.geo_fetching) + '</span>';

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;

                    // Use Nominatim API for reverse geocoding (free and no API key required)
                    fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`, {
                        headers: {
                            'User-Agent': 'MadesaStore/1.0'
                        }
                    })
                        .then(response => response.json())
                        .then(data => {
                            // Extract postcode from Nominatim response
                            const postcode = data.address?.postcode;

                            if (postcode) {
                                // Clean and validate CEP
                                const cleanCEP = postcode.replace(/\D/g, '');

                                if (cleanCEP.length === 8) {
                                    this.updateCEP(cleanCEP);
                                    this.closeModal(modal);
                                } else {
                                    throw new Error('CEP inválido obtido da localização');
                                }
                            } else {
                                throw new Error('CEP não encontrado para esta localização');
                            }
                        })
                        .catch(error => {
                            console.error('Erro no reverse geocoding:', error);
                            alert(this.str.geo_reverse_failed);
                        })
                        .finally(() => {
                            useLocationBtn.disabled = false;
                            useLocationBtn.innerHTML = originalText;
                        });
                },
                (error) => {
                    console.error('Erro ao obter localização:', error);

                    let errorMessage = this.str.geo_generic_error;

                    switch (error.code) {
                        case error.PERMISSION_DENIED:
                            errorMessage = this.str.geo_permission_denied;
                            break;
                        case error.POSITION_UNAVAILABLE:
                            errorMessage = this.str.geo_position_unavailable;
                            break;
                        case error.TIMEOUT:
                            errorMessage = this.str.geo_timeout;
                            break;
                    }

                    alert(errorMessage);

                    useLocationBtn.disabled = false;
                    useLocationBtn.innerHTML = originalText;
                }
            );
        }
    }

    // ========== Account Dropdown ==========
    initAccountDropdown() {
        const accountBtn = document.querySelector('[data-account-trigger]');
        const accountDropdown = document.querySelector('[data-account-dropdown]');

        if (!accountBtn || !accountDropdown) return;

        accountBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            accountDropdown.classList.toggle('madesa-hidden');
        });

        // Close dropdown when clicking outside
        document.addEventListener('click', (e) => {
            if (!accountBtn.contains(e.target) && !accountDropdown.contains(e.target)) {
                accountDropdown.classList.add('madesa-hidden');
            }
        });
    }

    // ========== Megamenu ==========
    initMegamenu() {
        const navLinks = document.querySelectorAll('[data-megamenu-trigger]');
        const megamenus = document.querySelectorAll('[data-megamenu]');

        if (!navLinks.length) return;

        let currentMegamenu = null;
        let closeTimeout = null;

        navLinks.forEach(link => {
            const targetId = link.dataset.megamenuTrigger;
            const megamenu = document.querySelector(`[data-megamenu="${targetId}"]`);

            if (!megamenu) return;

            // Show megamenu on hover
            link.addEventListener('mouseenter', () => {
                if (closeTimeout) {
                    clearTimeout(closeTimeout);
                    closeTimeout = null;
                }

                // Hide all other megamenus
                megamenus.forEach(m => {
                    if (m !== megamenu) {
                        m.classList.add('madesa-hidden');
                    }
                });

                // Remove active state from all nav links
                navLinks.forEach(l => l.classList.remove('active'));

                // Show current megamenu
                megamenu.classList.remove('madesa-hidden');
                link.classList.add('active');
                currentMegamenu = megamenu;
            });

            // Keep megamenu open when hovering over it
            megamenu.addEventListener('mouseenter', () => {
                if (closeTimeout) {
                    clearTimeout(closeTimeout);
                    closeTimeout = null;
                }
            });

            // Close megamenu when mouse leaves
            const handleMouseLeave = () => {
                closeTimeout = setTimeout(() => {
                    megamenu.classList.add('madesa-hidden');
                    link.classList.remove('active');
                    currentMegamenu = null;
                }, 200);
            };

            link.addEventListener('mouseleave', handleMouseLeave);
            megamenu.addEventListener('mouseleave', handleMouseLeave);
        });

        // Handle megamenu tabs (subcategories)
        const megamenuTabs = document.querySelectorAll('[data-megamenu-tab]');
        megamenuTabs.forEach(tab => {
            tab.addEventListener('click', () => {
                const targetId = tab.dataset.megamenuTab;
                const parentMegamenu = tab.closest('[data-megamenu]');
                const tabContents = parentMegamenu.querySelectorAll('[data-megamenu-content]');

                // Remove active state from all tabs
                parentMegamenu.querySelectorAll('[data-megamenu-tab]').forEach(t => {
                    t.classList.remove('active');
                });

                // Hide all tab contents
                tabContents.forEach(content => {
                    content.classList.add('madesa-hidden');
                });

                // Show active tab content
                const activeContent = parentMegamenu.querySelector(`[data-megamenu-content="${targetId}"]`);
                if (activeContent) {
                    activeContent.classList.remove('madesa-hidden');
                }

                tab.classList.add('active');
            });
        });
    }

    // ========== Mobile Menu ==========
    initMobileMenu() {
        const menuTrigger = document.querySelector('[data-mobile-menu-trigger]');
        const mobileMenu = document.querySelector('[data-mobile-menu]');
        const menuClose = document.querySelector('[data-mobile-menu-close]');

        if (!menuTrigger || !mobileMenu) return;

        menuTrigger.addEventListener('click', () => {
            this.openMobileMenu(mobileMenu);
        });

        if (menuClose) {
            menuClose.addEventListener('click', () => {
                this.closeMobileMenu(mobileMenu);
            });
        }

        // Handle mobile nav item with children
        const navItemsWithChildren = document.querySelectorAll('[data-mobile-nav-parent]');
        navItemsWithChildren.forEach(item => {
            item.addEventListener('click', (e) => {
                e.preventDefault();
                const submenu = item.nextElementSibling;
                if (submenu && submenu.hasAttribute('data-mobile-submenu')) {
                    submenu.classList.toggle('madesa-hidden');
                }
            });
        });
    }

    openMobileMenu(menu) {
        if (!menu) return;
        menu.classList.remove('madesa-hidden');
        document.body.style.overflow = 'hidden';
    }

    closeMobileMenu(menu) {
        if (!menu) return;
        menu.classList.add('madesa-hidden');
        document.body.style.overflow = '';
    }

    // ========== Countdown Timer ==========
    initCountdown() {
        const countdownElement = document.querySelector('[data-countdown]');
        if (!countdownElement) return;

        const endDateStr = countdownElement.dataset.countdown;
        if (!endDateStr) return;

        const endDate = new Date(endDateStr).getTime();

        const updateCountdown = () => {
            const now = new Date().getTime();
            const distance = endDate - now;

            if (distance < 0) {
                countdownElement.style.display = 'none';
                return;
            }

            const days = Math.floor(distance / (1000 * 60 * 60 * 24));
            const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((distance % (1000 * 60)) / 1000);

            const daysEl = countdownElement.querySelector('[data-countdown-days]');
            const hoursEl = countdownElement.querySelector('[data-countdown-hours]');
            const minutesEl = countdownElement.querySelector('[data-countdown-minutes]');
            const secondsEl = countdownElement.querySelector('[data-countdown-seconds]');

            if (daysEl) daysEl.textContent = String(days).padStart(2, '0');
            if (hoursEl) hoursEl.textContent = String(hours).padStart(2, '0');
            if (minutesEl) minutesEl.textContent = String(minutes).padStart(2, '0');
            if (secondsEl) secondsEl.textContent = String(seconds).padStart(2, '0');
        };

        // Update immediately
        updateCountdown();

        // Update every second
        setInterval(updateCountdown, 1000);
    }

    // ========== Helper Methods ==========
    openModal(modal) {
        if (!modal) return;
        modal.classList.remove('madesa-hidden');
        document.body.style.overflow = 'hidden';
    }

    closeModal(modal) {
        if (!modal) return;
        modal.classList.add('madesa-hidden');
        document.body.style.overflow = '';
    }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        new MadesaHeader();
    });
} else {
    new MadesaHeader();
}

// Export for use in other scripts if needed
if (typeof module !== 'undefined' && module.exports) {
    module.exports = MadesaHeader;
}

// Custom Element para Dropdown Menu
class DropdownMenu extends HTMLElement {
    constructor() {
        super();
        this.currentOpenDropdown = null;
    }

    connectedCallback() {
        this.setupEventListeners();
    }

    setupEventListeners() {
        // Adicionar eventos de clique para todos os dropdown toggles
        const dropdownToggles = this.querySelectorAll('[data-dropdown-toggle]');

        dropdownToggles.forEach(toggle => {
            toggle.addEventListener('click', (e) => this.handleToggleClick(e, toggle));
        });

        // Fechar dropdowns ao clicar fora
        document.addEventListener('click', (e) => {
            if (!this.contains(e.target)) {
                this.closeAllDropdowns();
            }
        });
    }

    handleToggleClick(event, toggle) {
        // Se for desktop, permite a navegação pelo link
        if (window.matchMedia('(min-width: 992px)').matches) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        const wrapper = toggle.nextElementSibling;
        const dropdown = wrapper?.querySelector('.dropdown-menu');

        if (!dropdown) return;

        const isCurrentlyOpen = dropdown.classList.contains('show');

        // Fechar dropdowns do mesmo nível
        this.closeSiblingDropdowns(toggle);

        // Toggle do dropdown atual
        if (isCurrentlyOpen) {
            this.closeDropdown(toggle, dropdown);
        } else {
            this.openDropdown(toggle, dropdown);
        }
    }

    closeSiblingDropdowns(currentToggle) {
        const parentLi = currentToggle.closest('li');
        const parentUl = parentLi.parentElement;
        const siblingItems = parentUl.querySelectorAll(':scope > li');

        siblingItems.forEach(item => {
            if (item !== parentLi) {
                const siblingToggle = item.querySelector(':scope > [data-dropdown-toggle]');
                if (siblingToggle) {
                    const siblingWrapper = siblingToggle.nextElementSibling;
                    const siblingDropdown = siblingWrapper?.querySelector('.dropdown-menu');
                    if (siblingDropdown?.classList.contains('show')) {
                        this.closeDropdown(siblingToggle, siblingDropdown);
                    }
                }
            }
        });
    }

    openDropdown(toggle, dropdown) {
        const wrapper = toggle.nextElementSibling;

        dropdown.classList.add('show');
        wrapper?.classList.add('show');
        toggle.setAttribute('aria-expanded', 'true');
        toggle.classList.add('show');
        this.currentOpenDropdown = dropdown;

        // Dispatch custom event
        this.dispatchEvent(new CustomEvent('dropdown-opened', {
            detail: { toggle, dropdown, wrapper },
            bubbles: true
        }));
    }

    closeDropdown(toggle, dropdown) {
        const wrapper = toggle.nextElementSibling;

        dropdown.classList.remove('show');
        wrapper?.classList.remove('show');
        toggle.setAttribute('aria-expanded', 'false');
        toggle.classList.remove('show');

        // Fechar todos os sub-dropdowns dentro deste dropdown
        const subDropdowns = dropdown.querySelectorAll('.dropdown-menu.show');
        const subWrappers = dropdown.querySelectorAll('.wrapper.show');

        subDropdowns.forEach(subDropdown => {
            subDropdown.classList.remove('show');
            const subWrapper = subDropdown.parentElement;
            subWrapper?.classList.remove('show');
            const subToggle = subWrapper?.previousElementSibling;
            if (subToggle) {
                subToggle.setAttribute('aria-expanded', 'false');
                subToggle.classList.remove('show');
            }
        });

        if (this.currentOpenDropdown === dropdown) {
            this.currentOpenDropdown = null;
        }

        // Dispatch custom event
        this.dispatchEvent(new CustomEvent('dropdown-closed', {
            detail: { toggle, dropdown, wrapper },
            bubbles: true
        }));
    }

    closeAllDropdowns() {
        const allDropdowns = this.querySelectorAll('.dropdown-menu.show');
        allDropdowns.forEach(dropdown => {
            const toggle = dropdown.closest('.wrapper')?.previousElementSibling;
            if (toggle) {
                this.closeDropdown(toggle, dropdown);
            }
        });
    }
}

// Registrar o custom element
if (!customElements.get('dropdown-menu')) {
    customElements.define('dropdown-menu', DropdownMenu);
}

// Custom Element para Lateral Menu Dropdown
class LateralMenuDropdown extends HTMLElement {
    constructor() {
        super();
        this.currentOpenDropdown = null;
    }

    connectedCallback() {
        this.setupEventListeners();
    }

    setupEventListeners() {
        const toggles = this.querySelectorAll('[data-lateral-toggle]');

        toggles.forEach(toggle => {
            toggle.addEventListener('click', (e) => this.handleToggleClick(e, toggle));
        });

        // Fechar dropdown ao clicar fora
        document.addEventListener('click', (e) => {
            if (!this.contains(e.target)) {
                this.closeAllDropdowns();
            }
        });
    }

    handleToggleClick(event, toggle) {
        event.preventDefault();
        event.stopPropagation();

        const parentLi = toggle.closest('.nav-item-lateral');
        const dropdown = parentLi?.querySelector('.sub-nav-links');

        if (!dropdown) return;

        const isCurrentlyOpen = dropdown.classList.contains('show');

        // Fechar outros dropdowns
        this.closeAllDropdowns();

        // Toggle do dropdown atual
        if (!isCurrentlyOpen) {
            this.openDropdown(toggle, dropdown);
        }
    }

    openDropdown(toggle, dropdown) {
        dropdown.classList.add('show');
        toggle.setAttribute('aria-expanded', 'true');
        toggle.classList.add('show');
        this.currentOpenDropdown = dropdown;
    }

    closeDropdown(toggle, dropdown) {
        dropdown.classList.remove('show');
        toggle?.setAttribute('aria-expanded', 'false');
        toggle?.classList.remove('show');

        if (this.currentOpenDropdown === dropdown) {
            this.currentOpenDropdown = null;
        }
    }

    closeAllDropdowns() {
        const allDropdowns = this.querySelectorAll('.sub-nav-links.show');
        allDropdowns.forEach(dropdown => {
            const parentLi = dropdown.closest('.nav-item-lateral');
            const toggle = parentLi?.querySelector('[data-lateral-toggle]');
            this.closeDropdown(toggle, dropdown);
        });
    }
}

// Registrar o custom element
if (!customElements.get('lateral-menu-dropdown')) {
    customElements.define('lateral-menu-dropdown', LateralMenuDropdown);
}

// ========== Mobile Menu Component ==========
class MadesaMobileMenu extends HTMLElement {
    constructor() {
        super();
        this.details = this.querySelector('.madesa-mobile-menu-container');
        this.closeButton = this.querySelector('.madesa-mobile-menu-close');
        this.submenuBackButtons = this.querySelectorAll('.madesa-mobile-submenu-back');

        this.init();
    }

    init() {
        if (!this.details) return;

        // Close button
        if (this.closeButton) {
            this.closeButton.addEventListener('click', (e) => {
                e.preventDefault();
                this.close();
            });
        }

        // Account button inside mobile menu
        const accountBtn = this.querySelector('.madesa-mobile-account-btn');
        if (accountBtn) {
            accountBtn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                // Open account modal without closing menu
                const accountModal = document.querySelector('[data-account-modal]');
                if (accountModal) {
                    accountModal.classList.add('active');
                    accountModal.classList.remove('madesa-hidden');
                }
            });
        }

        // Submenu back buttons
        this.submenuBackButtons.forEach(button => {
            button.addEventListener('click', (e) => {
                e.preventDefault();
                const details = button.closest('details');
                if (details) {
                    details.removeAttribute('open');
                }
            });
        });

        // Handle details toggle
        this.details.addEventListener('toggle', (e) => {
            if (this.details.open) {
                this.open();
            } else {
                this.onClose();
            }
        });

        // Close on escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.details.open) {
                this.close();
            }
        });

        // Close when clicking backdrop
        const summary = this.details.querySelector('summary');
        if (summary) {
            summary.addEventListener('click', (e) => {
                // If details is open and we're clicking the backdrop (not the button itself)
                if (this.details.open && e.target === summary) {
                    this.close();
                }
            });
        }
    }

    open() {
        document.body.classList.add('madesa-mobile-menu-open');
        this.trapFocus();
    }

    close() {
        this.details.removeAttribute('open');
    }

    onClose() {
        document.body.classList.remove('madesa-mobile-menu-open');
        this.removeFocusTrap();

        // Close all open submenus
        this.querySelectorAll('details[open]').forEach(detail => {
            if (detail !== this.details) {
                detail.removeAttribute('open');
            }
        });
    }

    trapFocus() {
        const focusableElements = this.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );

        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        this.handleTabKey = (e) => {
            if (e.key !== 'Tab') return;

            if (e.shiftKey) {
                if (document.activeElement === firstElement) {
                    e.preventDefault();
                    lastElement.focus();
                }
            } else {
                if (document.activeElement === lastElement) {
                    e.preventDefault();
                    firstElement.focus();
                }
            }
        };

        document.addEventListener('keydown', this.handleTabKey);
    }

    removeFocusTrap() {
        if (this.handleTabKey) {
            document.removeEventListener('keydown', this.handleTabKey);
        }
    }
}

// Register the custom element
if (!customElements.get('madesa-mobile-menu')) {
    customElements.define('madesa-mobile-menu', MadesaMobileMenu);
}
